import React from "react";
import {useAuthState} from "react-firebase-hooks/auth";

import {useFrame} from "@react-three/fiber";
import {Vec2} from "@repo/shared";

import useMyCamera from "~/contexts/camera/useMyCamera";
import {useGame} from "~/contexts/game-manager/GameManagerProvider";
import {auth} from "~/firebase/firebase";

const CameraControlsDuringPlay: React.FC<{
  following: boolean;
}> = ({following}) => {
  const [user] = useAuthState(auth);
  const {gameManager} = useGame();
  const {targetPosition} = useMyCamera();

  useFrame(() => {
    // --- Guard Clauses ---
    // Exit early if not following or if the game manager isn't ready.
    if (!following || !gameManager) {
      return;
    }

    // 1. Get all renderable players from the manager.
    const allPlayers = gameManager.getAllPlayers();

    // 2. Find the primary player to follow.
    let targetPlayers = allPlayers.filter(
      (p) =>
        p.isPrimary &&
        p.player.ownerId === user?.uid &&
        !p.player.isDead &&
        p.player.isActive,
    );
    if (targetPlayers.length === 0) {
      targetPlayers = allPlayers.filter(
        (p) =>
          p.isPrimary && p.player.ownerId === user?.uid && !p.player.isDead,
      );
      if (targetPlayers.length === 0) {
        targetPlayers = allPlayers.filter(
          (p) => p.isPrimary && !p.player.isDead && p.player.isActive,
        );
        if (targetPlayers.length === 0) {
          targetPlayers = allPlayers.filter(
            (p) => p.isPrimary && !p.player.isDead,
          );
          if (targetPlayers.length === 0) return;
        }
      }
    }

    const interpolatedPosition = targetPlayers
      .map(({smoothPlayerPosition}) => smoothPlayerPosition)
      .reduce((a, v) => a.add(v), Vec2.zero())
      .div(targetPlayers.length);

    // 5. Update the camera's target position ref.
    // The useMyCamera hook will handle moving the actual camera to this target.
    if (interpolatedPosition) {
      targetPosition.current.set(
        interpolatedPosition.x,
        interpolatedPosition.y,
      );
    }
  });

  return null; // This is a controller component, it doesn't render anything itself.
};

export default CameraControlsDuringPlay;
